import { Box, Typography } from '@mui/material'
import isNumber from 'lodash/isNumber'
import { useCallback, useEffect, useMemo } from 'react'
import { useFieldArray } from 'react-hook-form'
import { Resource } from 'utoniq-ui'

import {
  ItemMetadata,
  ItemSelectBoxFragment,
  ItemThumbnailType,
  ItemType,
  Maybe,
  PersonalInformationType,
  PreSale,
  Resource as ItemResource,
  SkuSettings,
  SkuSettingsInput,
  SkuUnitInput,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import {
  AppDeadlineInputCell,
  ContentsAudioInputCell,
  ExpirationDateInputCell,
  IssueInputCell,
  ItemImageInputCell,
  ItemThumbnailInputCell,
  LimitIssueInputCell,
  MusicApplicationInputCell,
  PageDirectionInputCell,
  PerOrderLimitInputCell,
  PersonalInformationRequirementsInputCell,
  personalInformationRequirementsValue,
  PriceInputCell,
  PrintInputCell,
  ResaleInputCell,
  ResourceUploadCell,
  ShareInputCell,
  SkuInputCell,
  TextInputCell,
  ThroughTicketInputCell,
  TicketNumberInputCell,
  ToggleInputCell,
} from '@/components/pages/ConsoleItemCreate/template/components'
import {
  CreateItemProps,
  FileUploadInfo,
} from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { config } from '@/config'
import {
  Form,
  handleError,
  tLabel,
  tMessage,
  useForm,
  UseFormProps,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Organism } from '@/core/architecture'
import { individualRemainingItemCountBySKU } from '@/core/individual'

export type ItemEditFormProps = {
  fileUploadInfo: FileUploadInfo
  onSubmit: (values: EditItemProps) => Promise<void>
  item: ItemSelectBoxFragment & {
    limitIssuedNumber?: number | null
    metadata?: Maybe<ItemMetadata>
    preSale?: Maybe<Pick<PreSale, 'id'>>
  }
}

export type EditItemProps = CreateItemProps & {
  editThumbnail?: boolean
  editThumbnailBack?: boolean
  editResource?: boolean
  editAvatarResource?: boolean
  editWallpaperResources?: boolean
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().label(tLabel('consoleItemEdit.title')).max(100),
  description: o.string().when('type', {
    is: ItemType.Ticket,
    then: o.string().optional().allow(null),
    otherwise: o
      .string()
      .max(1000)
      .label(tLabel('consoleItemEdit.description')),
  }),
  editResource: o.boolean().optional(),
  resourceId: o.string().when('editResource', {
    is: true,
    then: o.string().when('type', {
      switch: [
        {
          is: ItemType.Audio,
          then: o.string().label(tLabel('consoleItemEdit.resourceId.audio')),
        },
        {
          is: ItemType.Video,
          then: o
            .string()
            .label(tLabel('consoleItemEdit.resourceId.video'))
            .required(),
        },
        {
          is: ItemType.Magazine,
          then: o
            .string()
            .label(tLabel('consoleItemEdit.resourceId.magazine'))
            .required(),
        },
      ],
      otherwise: o.string().allow('').optional(),
    }),
    otherwise: o.string().allow('').optional(),
  }),
  resource: o.object().allow(null).optional(),
  editAvatarResource: o.boolean().optional(),
  avatarResources: o.object().when('editAvatarResource', {
    is: true,
    then: o.object().when('type', {
      is: ItemType.Avatar,
      then: o.object({
        avatarResourceId: o
          .string()
          .label(tLabel('consoleItemEdit.avatarResources.avatarResourceId'))
          .required(),
        voiceResourceId: o.string().allow('').optional(),
      }),
      otherwise: o.object().allow(null).optional(),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  avatarResource: o.object().allow(null).optional(),
  editWallpaperResources: o.boolean().optional(),
  wallpaperResources: o.object().when('editWallpaperResources', {
    is: true,
    then: o.object().when('type', {
      is: ItemType.Wallpaper,
      then: o.object({
        wallpaperResourceId: o
          .string()
          .label(
            tLabel('consoleItemEdit.wallpaperResources.wallpaperResourceId')
          )
          .required(),
        audioResourceId: o.string().allow('').optional(),
      }),
      otherwise: o.object().allow(null).optional(),
    }),
    otherwise: o.object().allow(null).optional(),
  }),
  wallpaperResource: o.object().allow(null).optional(),
  hasPrice: o.boolean().optional(),
  paymentType: o
    .string()
    .valid(UserPaymentType.PrepaidPoint, UserPaymentType.Cash)
    .allow(null)
    .optional(),
  price: o
    .number()
    .label(tLabel('consoleItemEdit.price'))
    .when('hasPrice', [
      { is: true, then: o.number().required().min(100).max(100000) },
      { is: false, then: o.number().allow(0).optional() },
    ]),
  hasIssuedNumber: o.boolean().optional(),
  issuedNumber: o
    .number()
    .label(tLabel('consoleItemEdit.issuedNumber'))
    .when('hasIssuedNumber', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  hideSalesNumber: o.boolean().optional(),
  hasPurchaseLimitByUser: o.boolean().optional(),
  purchaseLimitByUser: o
    .number()
    .label(tLabel('consoleItemEdit.purchaseLimitByUser'))
    .when('hasPurchaseLimitByUser', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  hasPurchaseLimitPerOrder: o.boolean().optional(),
  purchaseLimitPerOrder: o
    .number()
    .label(tLabel('consoleItemEdit.purchaseLimitPerOrder'))
    .when('hasPurchaseLimitPerOrder', [
      { is: true, then: o.number().required().min(1).max(100000) },
      { is: false, then: o.any().allow(null).optional() },
    ]),
  isPremium: o.boolean().label(tLabel('consoleItemEdit.isPremium')).optional(),
  personalInformationRequirements: o.when('type', {
    is: ItemType.Ticket,
    then: o
      .string()
      .optional()
      .label('consoleItemCreate.personalInformationRequirements'),
  }),
  type: o.string(),
  resaleEnabled: o
    .boolean()
    .optional()
    .label(tLabel('consoleItemEdit.resaleEnabled')),
  hasAssignedNumber: o.boolean().optional(),
  thumbnailType: o.string().allow('').optional(),
  editThumbnail: o.boolean().optional(),
  editThumbnailBack: o.boolean().optional(),
  thumbnail: o.string().when('editThumbnail', {
    is: true,
    then: o.string().label(tLabel('consoleItemEdit.thumbnail')),
    otherwise: o.string().allow('').optional(),
  }),
  thumbnailBack: o
    .string()
    .label(tLabel('consoleItemEdit.thumbnailBack'))
    .allow('')
    .optional(),
  metadata: o.object().when('type', {
    switch: [
      {
        is: ItemType.Merch,
        then: o.object({
          merch: o.object({
            receptionPeriod: o.object({
              endedAt: o
                .date()
                .label(
                  tLabel(
                    'consoleItemEdit.metadata.merch.receptionPeriod.endedAt'
                  )
                )
                .required(),
            }),
          }),
          magazine: o.object().optional(),
          ticket: o.object().optional(),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Ticket,
        then: o.object({
          merch: o.object().optional(),
          magazine: o.object().optional(),
          ticket: o.object({
            description: o
              .string()
              .max(1000)
              .label(tLabel('consoleItemEdit.metadata.ticket.description')),
            expirationTerm: o
              .string()
              .optional()
              .allow(null)
              .label(tLabel('consoleItemEdit.metadata.ticket.expirationTerm')),
            note: o
              .string()
              .optional()
              .allow(null)
              .label(tLabel('consoleItemEdit.metadata.ticket.note')),
            resalePeriod: o.object().when(
              // get relative references
              // https://joi.dev/api/?v=17.9.1#refkey-options
              o.ref('....resaleEnabled'),
              {
                is: true,
                then: o.object({
                  startedAt: o
                    .date()
                    .label(
                      tLabel(
                        'consoleItemEdit.metadata.ticket.resalePeriod.startedAt'
                      )
                    ),
                  endedAt: o
                    .date()
                    .label(
                      tLabel(
                        'consoleItemEdit.metadata.ticket.resalePeriod.endedAt'
                      )
                    ),
                }),
                otherwise: o.object({
                  startedAt: o
                    .date()
                    .optional()
                    .allow('')
                    .label(
                      tLabel(
                        'consoleItemEdit.metadata.ticket.resalePeriod.startedAt'
                      )
                    ),
                  endedAt: o
                    .date()
                    .optional()
                    .allow('')
                    .label(
                      tLabel(
                        'consoleItemEdit.metadata.ticket.resalePeriod.endedAt'
                      )
                    ),
                }),
              }
            ),
            assignedNumberPrefix: o
              .string()
              .when(o.ref('....hasAssignedNumber'), {
                is: true,
                then: o
                  .string()
                  .max(5)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.assignedNumberPrefix'
                    )
                  ),
                otherwise: o
                  .string()
                  .optional()
                  .allow(null)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.assignedNumberPrefix'
                    )
                  ),
              }),
            startAssignedNumber: o
              .string()
              .when(o.ref('....hasAssignedNumber'), {
                is: true,
                then: o
                  .string()
                  .max(8)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.startAssignedNumber'
                    )
                  ),
                otherwise: o
                  .string()
                  .optional()
                  .allow(null)
                  .label(
                    tLabel(
                      'consoleItemCreate.metadata.ticket.startAssignedNumber'
                    )
                  ),
              }),
            isThroughTicket: o.boolean().optional().allow(null),
            throughTicket: o.array().when('isThroughTicket', {
              is: true,
              then: o.array().items(
                o.object({
                  //id of field array
                  _id: o.string().optional(),
                  name: o
                    .string()
                    .label(
                      tLabel(
                        'consoleItemCreate.metadata.ticket.throughTicket.name'
                      )
                    ),
                })
              ),
              otherwise: o.array().optional().allow(null),
            }),
            distributionEnabled: o.boolean().optional().allow(null),
            distributionEndedAt: o.date().when('distributionEnabled', {
              is: true,
              then: o
                .date()
                .label(
                  tLabel('consoleItemEdit.metadata.ticket.distributionEndedAt')
                )
                .required(),
              otherwise: o.date().optional().allow(null, ''),
            }),
          }),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Magazine,
        then: o.object({
          ticket: o.object().optional(),
          merch: o.object().optional(),
          magazine: o.object({
            pageDirection: o
              .string()
              .label(
                tLabel('consoleItemCreate.metadata.magazine.pageDirection')
              ),
          }),
          wallpaper: o.object().optional(),
        }),
      },
      {
        is: ItemType.Wallpaper,
        then: o.object({
          ticket: o.object().optional(),
          merch: o.object().optional(),
          magazine: o.object().optional(),
          wallpaper: o.object({
            isPrintEnabled: o.boolean().optional().allow(null),
            printCount: o.number().optional().allow(null),
            printType: o.string().optional().allow(null),
          }),
        }),
      },
    ],
    otherwise: o.object().allow(null).optional(),
  }),
  thumbnailResource: o.object().allow(null).optional(),
  thumbnailBackResource: o.object().allow(null).optional(),
  hasSku: o.boolean().optional(),
  images: o.array().items(
    o.object({
      tmpPath: o.string().optional().allow(''),
      caption: o.string().optional(),
    })
  ),
  skuSettings: o.when('hasSku', {
    is: true,
    then: o.object({
      keyName: o.string().optional().allow(null),
      units: o.array().items(
        o.object({
          _id: o.string().optional(),
          id: o.string().optional(),
          unitName: o
            .string()
            .max(100)
            .label(tLabel('consoleItemEdit.unitName')),
          price: o.number().optional().allow(null),
          stock: o
            .number()
            .optional()
            .allow('')
            //check total stock less than issuedNumber
            .custom((value: number, helpers) => {
              const [, units, , data] = helpers?.state?.ancestors || []
              if (!value) {
                return value
              }
              const issuedNumber = data?.issuedNumber || 0
              const totalItemInStock = units?.reduce(
                (currentVal: number, unit: SkuUnitInput) =>
                  currentVal + +(unit?.stock || 0),
                0
              )
              if (totalItemInStock > issuedNumber) {
                return helpers.message({
                  custom: tMessage('consoleItemEdit.stock.custom.over'),
                })
              }
              if (totalItemInStock < issuedNumber) {
                return helpers.message({
                  custom: tMessage('consoleItemEdit.stock.custom.shortage'),
                })
              }
              return value
            }),
          order: o.number().optional(),
          thumbnail: o.string().optional().allow(null),
        })
      ),
    }),
    otherwise: o.object().optional(),
  }),
  isDownloadable: o
    .when('type', {
      is: ItemType.Wallpaper,
      then: o.boolean().allow(null).optional(),
      otherwise: o.forbidden(),
    })
    .label(tLabel('consoleItemCreate.isDownloadable')),
})

export const ItemEditForm: Organism<ItemEditFormProps> = ({
  item,
  fileUploadInfo,
  onSubmit,
}) => {
  const { showMessage, showError } = useMessage()
  const { router, generatePath, routerPages } = useRouter()
  const defaultBackPage = generatePath(routerPages.ConsoleItemDetail, {
    itemId: item.id ?? '#',
  })
  const { t } = useTranslation('consoleItemEdit')
  const { type: resourceType } = item
  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
    setValue,
    watch,
    reset,
    setError,
    control,
    trigger,
  } = useForm<EditItemProps>({
    schema,
    defaultValues: {
      ...defaultValues,
    },
  })

  const hasPurchased = useMemo(() => {
    if (!item) return false
    return item.purchasedNumber + (item.pendingPurchaseNumber ?? 0) > 0
  }, [item])

  const {
    fields,
    append,
    remove: onRemove,
    move: onMove,
  } = useFieldArray({
    control,
    keyName: '_id',
    name: 'skuSettings.units',
  })

  useEffect(() => {
    reset({
      title: item.title,
      type: item.type,
      description: item.description,
      isPremium: item.isPremium,
      price: item.price,
      issuedNumber: item.issuedNumber,
      thumbnailType: item.thumbnailType,
      thumbnail: '',
      thumbnailBack: '',
      hasPrice: item.price > 0,
      paymentType: item.paymentType,
      hasIssuedNumber: item.issuedNumber !== null,
      isDownloadable:
        item.isDownloadable === null ? undefined : item.isDownloadable,
      purchaseLimitByUser: item.purchaseLimitByUser,
      hasPurchaseLimitByUser: isNumber(item.purchaseLimitByUser),
      purchaseLimitPerOrder: item.purchaseLimitPerOrder,
      hasPurchaseLimitPerOrder: isNumber(item.purchaseLimitPerOrder),
      personalInformationRequirements: item.personalInformationRequirements
        ?.length
        ? item.personalInformationRequirements?.includes(
            'address' as PersonalInformationType
          )
          ? personalInformationRequirementsValue.nameAndTelAndAddress
          : personalInformationRequirementsValue.nameAndTel
        : personalInformationRequirementsValue.none,
      resaleEnabled: !!item.resaleEnabled,
      hideSalesNumber: item.hideSalesNumber || false,
      hasSku: !!item.skuSettings?.units.length,
      hasAssignedNumber: !!item.metadata?.ticket?.assignedNumberPrefix,
      skuSettings: buildSkuSettings(item.skuSettings),
      images: Array.from(Array(30).keys()).map((_, index) => ({
        tmpPath: item?.images?.[index]?.uri ?? '',
        caption: item?.images?.[index]?.caption || undefined,
      })),
      ...buildMetadata(item.metadata, item.type),
      ...buildItemResources(item),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  const handleOnSubmit = useCallback(
    async (values: CreateItemProps) => {
      if (!isDirty) {
        router.goBack(defaultBackPage)
        return
      }

      return onSubmit(values)
        .then(() => {
          showMessage(t('editItemSuccessful'), 'success')
          router.goBack(defaultBackPage)
        })
        .catch(error => {
          handleError<EditItemProps>(error, setError, showError)
        })
    },
    [isDirty, onSubmit, router, showMessage, t, setError, showError]
  )

  return (
    <Form
      id="item-edit-form"
      sx={{ py: 5 }}
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      {resourceType !== ItemType.Ticket && (
        <ResourceUploadCell
          watch={watch}
          resourceType={resourceType}
          setValue={setValue}
          fileUploadInfo={fileUploadInfo}
          error={
            errors.resourceId ||
            errors.avatarResources?.avatarResourceId ||
            errors.wallpaperResources?.wallpaperResourceId
          }
          hasCreateResource={false}
          onClose={resourceType => {
            switch (resourceType) {
              case ItemType.Wallpaper:
                setValue('editWallpaperResources', true, {
                  shouldValidate: true,
                })
                break
              case ItemType.Avatar:
                setValue('editAvatarResource', true, { shouldValidate: true })
                break
              default:
                setValue('editResource', true, { shouldValidate: true })
                break
            }
          }}
        />
      )}
      {resourceType === ItemType.Magazine && (
        <PageDirectionInputCell setValue={setValue} watch={watch} />
      )}
      {resourceType === ItemType.Wallpaper && (
        <ToggleInputCell
          content={{
            title: t('isDownloadable'),
            description: t('isDownloadableDesc'),
          }}
          checked={watch('isDownloadable')}
          sx={{
            '& > div:last-child span': {
              typography: 'body2',
            },
          }}
          {...register('isDownloadable')}
        />
      )}
      {resourceType === ItemType.Wallpaper && config.modules.eprint && (
        <PrintInputCell
          setValue={setValue}
          isPrintEnabled={watch('metadata.wallpaper.isPrintEnabled')}
          printType={watch('metadata.wallpaper.printType')}
          printCount={watch('metadata.wallpaper.printCount')}
        />
      )}
      {(resourceType === ItemType.Wallpaper ||
        resourceType === ItemType.Avatar) && (
        <ContentsAudioInputCell
          resourceType={resourceType}
          watch={watch}
          setValue={setValue}
          audioUploadInfo={fileUploadInfo}
          hasCreateResource={false}
        />
      )}
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-5759
       */}
      {resourceType === ItemType.Audio && false && (
        <MusicApplicationInputCell />
      )}
      <ItemThumbnailInputCell
        isEdit
        watch={watch}
        setValue={setValue}
        resourceType={resourceType}
        itemUploadInfo={fileUploadInfo}
        error={errors.thumbnail}
        errorBack={errors.thumbnailBack}
        onClose={() => {
          setValue('editThumbnail', true, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        onBackClose={() => {
          setValue('editThumbnailBack', true, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
      />
      <ItemImageInputCell
        watch={watch}
        setValue={setValue}
        control={control}
        fileUploadInfo={fileUploadInfo}
      />
      <TextInputCell type="text" error={errors.title} {...register('title')} />
      {(config.modules.store || config.modules.preSale) && (
        <PriceInputCell
          mode="edit"
          resourceType={resourceType}
          hasPrice={watch('hasPrice')}
          setValue={setValue}
          paymentType={watch('paymentType') ?? null}
          defaultPrice={item.price ?? 0}
          disableRadio={!!item.preSale?.id}
          inputProps={{
            type: 'number',
            error: errors.price,
            ...register('price'),
          }}
        />
      )}
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-9983
       */}
      {resourceType === ItemType.Ticket && (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.metadata?.ticket?.description}
          {...register('metadata.ticket.description')}
        />
      )}
      {resourceType !== ItemType.Ticket && (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.description}
          {...register('description')}
        />
      )}
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-9983
       */}
      {resourceType === ItemType.Ticket && (
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 5,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          error={errors.metadata?.ticket?.note}
          {...register('metadata.ticket.note')}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <ThroughTicketInputCell
          editable={!hasPurchased}
          isThroughTicket={watch('metadata.ticket.isThroughTicket')}
          control={control}
          watch={watch}
          setValue={setValue}
          register={register}
          errors={errors.metadata?.ticket?.throughTicket}
        />
      )}
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-9983
       */}
      {resourceType === ItemType.Ticket && (
        <ExpirationDateInputCell
          setValue={setValue}
          value={watch('metadata.ticket.expirationTerm')}
          error={errors?.metadata?.ticket?.expirationTerm}
        />
      )}
      {resourceType === ItemType.Merch && (
        <AppDeadlineInputCell
          setValue={setValue}
          error={errors.metadata?.merch?.receptionPeriod?.endedAt}
          value={watch('metadata.merch.receptionPeriod.endedAt')}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <PersonalInformationRequirementsInputCell
          {...register('personalInformationRequirements')}
          personalInformationRequirements={watch(
            'personalInformationRequirements'
          )}
          setValue={setValue}
        />
      )}
      <IssueInputCell
        hasIssuedNumber={watch('hasIssuedNumber')}
        defaultNumber={item.issuedNumber ?? 100}
        setValue={setValue}
        inputProps={{
          type: 'number',
          error: errors.issuedNumber,
          ...register('issuedNumber'),
          async onChange(event) {
            setValue('issuedNumber', event.target.value as never)
            await Promise.all(
              [...Array(fields.length).keys()].map(idx =>
                trigger(`skuSettings.units.${idx}.stock`)
              )
            )
          },
        }}
      />
      {/* 全体残り数量の表示 */}
      {item.issuedNumber && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '-32px',
            mb: '32px',
          }}
        >
          <Typography variant="subtitle2">{t('remainingNumber')}</Typography>
          <Typography variant="subtitle2">
            {(watch('issuedNumber') || 0) -
              item.purchasedNumber -
              (item?.pendingPurchaseNumber || 0)}
          </Typography>
        </Box>
      )}
      <SkuInputCell
        setValue={setValue}
        hasIssuedNumber={watch('hasIssuedNumber')}
        fields={fields}
        watch={watch}
        trigger={trigger}
        register={register}
        onRemove={onRemove}
        fileUploadInfo={fileUploadInfo}
        onMove={onMove}
        errors={errors.skuSettings}
        units={
          individualRemainingItemCountBySKU() && item.issuedNumber
            ? item.skuSettings?.units
            : undefined
        }
        onAppend={() =>
          append({
            unitName: '',
            stock: undefined,
            order: 1,
            thumbnail: undefined,
          })
        }
      />
      <LimitIssueInputCell
        hasPurchaseLimitByUser={watch('hasPurchaseLimitByUser')}
        defaultNumber={item.limitIssuedNumber ?? 1}
        setValue={setValue}
        inputProps={{
          type: 'number',
          error: errors.purchaseLimitByUser,
          ...register('purchaseLimitByUser'),
        }}
      />
      {config.modules.cart && config.modules.pickup && (
        <PerOrderLimitInputCell
          hasPurchaseLimitPerOrder={watch('hasPurchaseLimitPerOrder')}
          defaultNumber={item.purchaseLimitPerOrder ?? 1}
          setValue={setValue}
          inputProps={{
            type: 'number',
            error: errors.purchaseLimitPerOrder,
            ...register('purchaseLimitPerOrder'),
          }}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <TicketNumberInputCell
          watch={watch}
          setValue={setValue}
          register={register}
          errors={errors?.metadata?.ticket}
        />
      )}
      {resourceType === ItemType.Ticket && (
        <ShareInputCell
          setValue={setValue}
          value={!!watch('metadata.ticket.distributionEnabled')}
          endedAt={watch('metadata.ticket.distributionEndedAt')}
          errors={errors}
        />
      )}
      {config.modules.membership && (
        <ToggleInputCell
          content={{ title: t('membersOnly') }}
          sx={{ px: 0, pt: 0 }}
          checked={watch('isPremium')}
          {...register('isPremium')}
        />
      )}
      <ToggleInputCell
        content={{
          title: t('hideSalesNumberLabel'),
          description: t('hideSalesNumberDesc'),
        }}
        checked={watch('hideSalesNumber')}
        sx={{
          '& > div:last-child span': {
            typography: 'body2',
          },
        }}
        {...register('hideSalesNumber')}
      />
      {resourceType === ItemType.Ticket && (
        <ResaleInputCell
          setValue={setValue}
          value={!!watch('resaleEnabled')}
          startedAt={watch('metadata.ticket.resalePeriod.startedAt')}
          endedAt={watch('metadata.ticket.resalePeriod.endedAt')}
          errors={errors}
        />
      )}
    </Form>
  )
}

const defaultValues: EditItemProps = {
  title: '',
  type: ItemType.Image,
  description: '',
  isPremium: false,
  personalInformationRequirements: personalInformationRequirementsValue.none,
  price: 0,
  issuedNumber: null,
  hideSalesNumber: false,
  thumbnail: '',
  copyrights: [],
  hasPrice: false,
  resaleEnabled: false,
  hasIssuedNumber: false,
  hasPurchaseLimitByUser: false,
  purchaseLimitByUser: null,
  hasPurchaseLimitPerOrder: false,
  purchaseLimitPerOrder: null,
  thumbnailType: ItemThumbnailType.Rectangle,
  metadata: {
    merch: { receptionPeriod: { endedAt: null } },
    ticket: {
      description: null,
      note: null,
      expirationTerm: null,
      resalePeriod: {
        startedAt: null,
        endedAt: null,
      },
      startAssignedNumber: null,
      assignedNumberPrefix: null,
      isThroughTicket: null,
      throughTicket: null,
      distributionEnabled: false,
      distributionEndedAt: null,
    },
  },
  images: Array.from(Array(30).keys()).map(() => ({
    tmpPath: '',
    caption: undefined,
  })),
  resource: null,
  avatarResource: null,
  wallpaperResource: null,
  thumbnailResource: null,
  thumbnailBackResource: null,
  skuSettings: null,
  hasSku: false,
  isDownloadable: undefined,
  hasAssignedNumber: false,
}

function buildItemResources(
  item: ItemSelectBoxFragment
): Pick<
  EditItemProps,
  'resource' | 'avatarResource' | 'wallpaperResource' | 'resourceId'
> {
  const resources = {
    thumbnailResource: buildResource(item.thumbnail.front),
    thumbnailBackResource: buildResource(item.thumbnail.back),
    resource: buildResource(item?.resource),
    wallpaperResource: null,
    avatarResource: null,
  }
  if (item.type === ItemType.Wallpaper) {
    return {
      ...resources,
      resource: buildResource(item?.wallpaperResources?.audioResource),
      wallpaperResource: buildResource(
        item?.wallpaperResources?.wallpaperResource
      ),
    }
  }
  if (item.type === ItemType.Avatar) {
    return {
      ...resources,
      resource: buildResource(item?.avatarResources?.voiceResource),
      avatarResource: buildResource(item?.avatarResources?.avatarResource),
    }
  }
  return resources
}

function buildSkuSettings(skuSettings?: Maybe<SkuSettings>): SkuSettingsInput {
  if (!skuSettings) {
    return { keyName: undefined, units: [] }
  }
  return {
    keyName: skuSettings.keyName,
    units: skuSettings.units.map(
      ({ id, unitName, thumbnail, stock, order, price }) => ({
        id,
        unitName,
        thumbnail: thumbnail?.uri || null,
        stock,
        order,
        price: price ?? null,
      })
    ),
  }
}

function buildMetadata(
  metadata: Maybe<ItemMetadata> | undefined,
  type: ItemType
): {
  metadata?: ItemMetadata
} {
  if (!metadata) return {}
  return {
    metadata: {
      ticket: metadata.ticket
        ? {
            description: metadata.ticket?.description ?? null,
            note: metadata.ticket?.note ?? null,
            expirationTerm: metadata.ticket?.expirationTerm ?? null,
            assignedNumberPrefix: metadata.ticket.assignedNumberPrefix
              ? metadata.ticket.assignedNumberPrefix
              : null,
            startAssignedNumber: metadata.ticket.assignedNumberPrefix
              ? (metadata.ticket.startAssignedNumber?.toString() as never)
              : null,
            resalePeriod:
              type === ItemType.Ticket
                ? {
                    startedAt: metadata.ticket?.resalePeriod?.startedAt || '',
                    endedAt: metadata.ticket?.resalePeriod?.endedAt || '',
                  }
                : undefined,
            isThroughTicket: metadata.ticket.isThroughTicket,
            throughTicket:
              metadata.ticket.throughTicket?.map(({ name }) => ({ name })) ??
              [],
            distributionEndedAt: metadata.ticket?.distributionEndedAt ?? null,
            distributionEnabled: !!metadata.ticket?.distributionEnabled,
          }
        : undefined,
      merch: metadata.merch
        ? {
            receptionPeriod: {
              endedAt: metadata.merch.receptionPeriod?.endedAt,
            },
          }
        : undefined,
      magazine: metadata.magazine
        ? {
            pageDirection: metadata.magazine.pageDirection,
          }
        : undefined,
      wallpaper: metadata.wallpaper
        ? {
            isPrintEnabled: !!metadata.wallpaper.isPrintEnabled,
            printType: metadata.wallpaper.printType ?? null,
            printCount: metadata.wallpaper.printCount ?? null,
          }
        : undefined,
    },
  }
}

function buildResource(
  resource?: Maybe<Partial<ItemResource>>
): Resource | null {
  if (!resource) {
    return null
  }
  return {
    id: resource.name ?? '',
    resourceName: resource.name ?? '',
    imageUrl: resource.uri ?? '',
    imageUploadPath: undefined, // 変更したときみpathを入れる
  }
}
